import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "./lib/auth";
import { ThemeProvider } from "./lib/theme";
import { LoginPromptProvider, useLoginPrompt } from "./lib/loginPrompt";
import { setDemoMode, onLoginRequired } from "./lib/api";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Layout from "./components/Layout";
import Loading from "./components/Loading";
import Dashboard from "./pages/Dashboard";
import Leads from "./pages/Leads";
import LeadDetail from "./pages/LeadDetail";
import Trials from "./pages/Trials";
import Members from "./pages/Members";
import MemberDetail from "./pages/MemberDetail";
import Automations from "./pages/Automations";
import Settings from "./pages/Settings";
import Checkin from "./pages/Checkin";
import CalendarPage from "./pages/Calendar";
import MemberPortal from "./pages/MemberPortal";
import AIPage from "./pages/AIPage";
import Tasks from "./pages/Tasks";
import Landing from "./pages/Landing";
import AdminDashboard from "./pages/AdminDashboard";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import RefundPolicy from "./pages/RefundPolicy";
import Contact from "./pages/Contact";
import About from "./pages/About";
import ScrollToHash from "./components/ScrollToHash";

/**
 * AppRoute - wraps page routes with Layout. Allows BOTH logged-in users (full
 * data) and demo-mode visitors (sample data). Pure logged-out (no demo user
 * fallback) → redirect to /login.
 */
function AppRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

/**
 * Sync the API demo flag + login-required hook with auth state.
 * Must live INSIDE AuthProvider + LoginPromptProvider.
 */
function DemoBridge() {
  const { isDemo } = useAuth();
  const { requireLogin } = useLoginPrompt();
  useEffect(() => {
    setDemoMode(isDemo);
  }, [isDemo]);
  useEffect(() => {
    onLoginRequired(() => requireLogin("Login to perform this action"));
  }, [requireLogin]);
  return null;
}

function AppRouter() {
  return (
    <>
      <ScrollToHash />
      <DemoBridge />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/me/:code" element={<MemberPortal />} />
        <Route path="/" element={<Landing />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about" element={<About />} />
        <Route path="/dashboard" element={<AppRoute><Dashboard /></AppRoute>} />
        <Route path="/tasks" element={<AppRoute><Tasks /></AppRoute>} />
        <Route path="/leads" element={<AppRoute><Leads /></AppRoute>} />
        <Route path="/leads/:id" element={<AppRoute><LeadDetail /></AppRoute>} />
        <Route path="/trials" element={<AppRoute><Trials /></AppRoute>} />
        <Route path="/members" element={<AppRoute><Members /></AppRoute>} />
        <Route path="/members/:id" element={<AppRoute><MemberDetail /></AppRoute>} />
        <Route path="/checkin" element={<AppRoute><Checkin /></AppRoute>} />
        <Route path="/calendar" element={<AppRoute><CalendarPage /></AppRoute>} />
        <Route path="/automations" element={<AppRoute><Automations /></AppRoute>} />
        <Route path="/ai" element={<AppRoute><AIPage /></AppRoute>} />
        <Route path="/settings" element={<AppRoute><Settings /></AppRoute>} />
        <Route path="/admin" element={<AppRoute><AdminDashboard /></AppRoute>} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <LoginPromptProvider>
            <AppRouter />
            <Toaster position="top-center" richColors />
          </LoginPromptProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
